import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { trpc } from "@blis/trpc/react";

import { useCommandMenu } from "@/components/CommandMenu";
import { ActiveHoldIndicator } from "@/components/Indicators";
import { Badge } from "@/components/ui/badge";
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { StatusIcon } from "@/components/ui/icons";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import type { AccessionRouter } from "@/lib/types";
import { format } from "@/lib/utils";
import { useAccession } from "../AccessionProvider";

type RelatedAccession = AccessionRouter["related"][0];

export function RelatedAccessionsCommand() {
  const { accession } = useAccession();
  const { setLoading } = useCommandMenu();

  const { data, isPending } = trpc.accession.related.useQuery({
    subjectId: accession.subjectId,
  });

  useEffect(() => {
    setLoading(isPending);
  }, [isPending, setLoading]);

  const filtered = data?.filter((acc) => acc.id !== accession.id);

  return (
    <CommandGroup heading="Related accessions">
      <CommandEmpty>No related accessions</CommandEmpty>
      {isPending &&
        new Array(3).fill(undefined).map((_, idx) => (
          <CommandItem key={idx}>
            <div className="flex w-full justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="w-20" />
                <Skeleton shape="circle" />
                <Skeleton className="w-20" />
              </div>
              <Skeleton className="w-20" />
            </div>
          </CommandItem>
        ))}
      {filtered?.map((acc) => (
        <RelatedAccessionCommandItem key={acc.id} accession={acc} />
      ))}
    </CommandGroup>
  );
}

function RelatedAccessionCommandItem({
  accession,
}: {
  accession: RelatedAccession;
}) {
  const navigate = useNavigate();
  const { setOpen } = useCommandMenu();

  const value = `${accession.id.toString()} ${accession.caseId} ${
    accession.unosId ?? ""
  } ${accession.testType}`;

  return (
    <CommandItem
      key={accession.id}
      value={value}
      onSelect={() => {
        navigate({
          to: "/accessions/$id",
          params: { id: accession.id },
          state: {
            href: { to: "/accessions" },
          },
        });
        setOpen(false);
      }}
      className="items-center justify-between gap-2"
    >
      <div className="flex items-center gap-2">
        <Text muted mono bold size="xs">
          {accession.id}
        </Text>
        <StatusIcon status={accession.status ?? "new"} className="shrink-0" />
        <Text size="1">{format(accession.date)}</Text>
        {accession.hasHold && <ActiveHoldIndicator />}
      </div>
      <Badge size="sm">{accession.testType}</Badge>
    </CommandItem>
  );
}
